import { LoadingButton } from '@mui/lab'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material'
import { usersDeleteAuthenticated } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { COLORS } from 'app-constants'
import JelledModal, { ModalButtonsContainerStack } from 'components/JelledModal'
import { useAuthToken } from 'hooks/use-auth-token'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { useActiveOrganization } from 'hooks/use-organization'
import { useState } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { authenticatedUserAtom, toastAtom } from 'state'
import { isEmailAddressValid } from 'utils'

const RequestDeleteModal = ({
  onClose,
  onOpenConfirmDeletionDialog,
  isOpen,
}: {
  onClose: () => void
  onOpenConfirmDeletionDialog: () => void
  isOpen: boolean
}) => {
  const isMobile = useCheckMobileScreen()
  const setToast = useSetRecoilState(toastAtom)

  const user = useRecoilValue(authenticatedUserAtom)

  const [feedback, setFeedback] = useState<string>('')
  const onFeedbackChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFeedback(event.target.value)
  }

  const [email, setEmail] = useState<string>('')
  const onEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value)
  }

  const [submitCount, setSubmitCount] = useState<number>(0)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const onRequestDeletionClick = async () => {
    if (!isEmailAddressValid(email) || email !== user?.email) {
      setSubmitCount(submitCount + 1)
      return
    }

    setIsSubmitting(true)
    try {
      await usersDeleteAuthenticated({
        feedback: feedback ? feedback : undefined,
      })
      onClose()
      onOpenConfirmDeletionDialog()
    } catch (error) {
      setToast({
        title: 'Failed to delete the account at this time, please try again later',
        severity: 'error',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <JelledModal
      isOpen={isOpen}
      onClose={onClose}
      width={isMobile ? '100%' : '675px'}
      modalStyle={{
        backgroundColor: COLORS.PURE_WHITE,
      }}>
      <Stack mb={3}>
        <Typography variant="h4" color="secondary" mb={3}>
          Delete account
        </Typography>

        <Typography variant="pS">Please let us know why you want to delete your account:</Typography>

        <TextField
          value={feedback}
          onChange={onFeedbackChange}
          multiline
          rows={4}
          fullWidth
          placeholder="Anything you want to share?"
          sx={{
            mt: 1,
            mb: 3,
          }}
        />

        <Typography
          variant="pS"
          sx={{
            color: COLORS.GRAY_1,
          }}>
          Verify your email to delete your account and all associated data in Jelled. Upon request, your account will be
          deleted within 48 hours. This cannot be undone.
        </Typography>

        <TextField
          value={email}
          onChange={onEmailChange}
          fullWidth
          error={submitCount > 0 && (email !== user?.email || !isEmailAddressValid)}
          helperText={
            submitCount > 0 && (!isEmailAddressValid(email) || email !== user?.email)
              ? 'Please enter the correct email'
              : ''
          }
          placeholder="name@domain.org"
          sx={{
            mt: 1,
            mb: 1,
          }}
        />

        {user && (
          <Typography
            variant="pXS"
            sx={{
              color: COLORS.RED,
            }}>
            Enter {user.email}
          </Typography>
        )}
      </Stack>

      <ModalButtonsContainerStack>
        <Button
          onClick={onClose}
          size="small"
          variant="text"
          sx={{
            color: COLORS.GRAY_3,
          }}>
          Cancel
        </Button>
        <LoadingButton
          loading={isSubmitting}
          size="small"
          variant="contained"
          color="error"
          onClick={onRequestDeletionClick}
          sx={{
            background: COLORS.RED,
            '&:hover': {
              background: COLORS.RED_HOVERED,
            },
          }}>
          Request deletion
        </LoadingButton>
      </ModalButtonsContainerStack>
    </JelledModal>
  )
}

const DeleteAccount = () => {
  const [isRequestDeleteModalOpen, setIsRequestDeleteModalOpen] = useState(false)
  const [isDeleteConfirmationDialogOpen, setIsDeleteConfirmationDialogOpen] = useState(false)

  const [, setAuthToken] = useAuthToken()
  const [, setActiveOrganization] = useActiveOrganization()

  const onCloseClick = () => {
    setAuthToken(null)
    setActiveOrganization(null)

    // clear session storage
    sessionStorage.clear()
  }

  return (
    <Stack direction="row" alignItems="flex-start" spacing={12.5}>
      <Typography variant="h5">Delete account</Typography>

      <Stack spacing={2}>
        <Typography variant="pS">Permanently delete your account and data. This cannot be undone.</Typography>

        <Button
          variant="contained"
          onClick={() => setIsRequestDeleteModalOpen(true)}
          sx={{
            background: COLORS.RED,
            color: COLORS.PURE_WHITE,
            width: 'fit-content',
            '&:hover': {
              background: COLORS.RED_HOVERED,
            },
          }}>
          Delete account
        </Button>
      </Stack>

      {isRequestDeleteModalOpen && (
        <RequestDeleteModal
          onClose={() => setIsRequestDeleteModalOpen(false)}
          onOpenConfirmDeletionDialog={() => setIsDeleteConfirmationDialogOpen(true)}
          isOpen={isRequestDeleteModalOpen}
        />
      )}

      {isDeleteConfirmationDialogOpen && (
        <Dialog open={true} onClose={onCloseClick}>
          <DialogTitle>Your request has been successfully sent.</DialogTitle>
          <DialogContent
            sx={{
              mt: 3,
            }}>
            <Typography
              variant="pS"
              sx={{
                color: COLORS.GRAY_1,
              }}>
              Your account and all associated data in Jelled will be deleted within 48 hours.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button size="small" variant="contained" color="primary" onClick={onCloseClick}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Stack>
  )
}

export default DeleteAccount
